import {
  createEffect,
  createMemo,
  createUniqueId,
  For,
  type JSX,
  onCleanup,
  Show,
  untrack,
} from 'solid-js';
import { t } from '../i18n';
import { isTopLayer, popLayer, pushLayer } from '../lib/dialog-trap';
import type { PagedGroup } from '../lib/fleet-machines';
import { MAP_LARGE_AT, type MapMode, mapLegend } from '../lib/fleet-map';
import type { UtilBox } from '../lib/fleetUtilization';
import type { MachineHandlers } from '../lib/machine-panel';
import { createMapTip } from '../lib/map-tip';
import { MachineGroups } from './MachineGroups';
import { MachineMapLegend } from './MachineMapLegend';
import { MachineMapSquare } from './MachineMapSquare';
import { MachineMapTip } from './MachineMapTip';

// The Machines list's Map view (#6562, prototype `mapView`): one square per machine,
// grouped like the list (`MachineGroups`' `body`, so the same headers and the same keyed
// walk: the 20-second poll recolours a square in place), coloured by the "Show" switch,
// then the legend with its counts. Squares are large at 60 machines or fewer in the fleet.
// Presenter: every rule is `lib/fleet-map.ts`'s.
//
// ONE TOOLTIP FOR THE WHOLE MAP (`MachineMapTip`), not one per square, and the square's
// description while it shows. What holds it open, and what dismisses it (Escape, a click
// outside it), is `lib/map-tip.ts`'s (WCAG 1.4.13).
//
// SELECTING A SQUARE OPENS THE MACHINE'S DETAILS PANEL (#6563) for every viewer and every
// machine (owner ruling 2026-10-22): the one way in on a phone. The panel is read-only for
// a member, a viewer or a machine from another org (owner rulings G21, G23).

export type MachineMapProps = {
  readonly paged: readonly PagedGroup[];
  readonly byId: ReadonlyMap<string, UtilBox>;
  /** The whole fleet: the legend counts it and the square size reads its size. */
  readonly boxes: readonly UtilBox[];
  readonly mode: MapMode;
  readonly canWrite: boolean;
  readonly onManage: MachineHandlers['onManage'];
  readonly onRename: (box: UtilBox) => void;
  readonly onDrain: (box: UtilBox) => void;
  readonly onUndrain: (box: UtilBox) => void;
  readonly onRemove: (box: UtilBox) => void;
  /**
   * The three the group header's server-name write needs, threaded to
   * `MachineGroups`: the UNFILTERED roster (the PUT names every machine on the
   * server, not the filtered rows), the token, and the write's toast + re-read.
   */
  readonly allBoxes: readonly UtilBox[];
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
};

export function MachineMap(props: MachineMapProps): JSX.Element {
  const tipId = createUniqueId();
  const { tip, hover, hoverTip, focus, close, prune } = createMapTip();

  const large = (): boolean => props.boxes.length <= MAP_LARGE_AT;

  const select = (b: UtilBox): void => {
    close();
    props.onManage(b);
  };

  // Escape, or a click anywhere but the tooltip and its own square, dismisses it wherever
  // focus or the pointer is. A click on the tooltip's own square is left to the square:
  // `select` closes the tooltip and opens the details panel. A click, not a pointerdown: a touch scroll that starts
  // outside the tooltip fires a pointerdown and no click, and must not dismiss the details it
  // scrolls along with. The listeners follow open / shut only, and read the open tooltip when
  // an event arrives, so a claim changing does not re-register them.
  // Escape follows the Popover rule (`Popover.tsx`): the open tooltip is a layer on the
  // shared overlay stack, so a dialog or menu opened above it owns the key, and a key a
  // handler already consumed is not this one's. Without both, one Escape closed a dialog
  // AND the tooltip under it.
  const shown = createMemo(() => tip() !== null);
  createEffect(() => {
    if (!shown()) return;
    const layer = pushLayer();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || !isTopLayer(layer)) return;
      e.preventDefault();
      close();
    };
    const onClick = (e: MouseEvent): void => {
      const open = tip();
      const target = e.target instanceof Node ? e.target : null;
      if (open === null || target === null) return;
      const inside =
        open.anchor.contains(target) || document.getElementById(tipId)?.contains(target) === true;
      if (!inside) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick, true);
    onCleanup(() => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick, true);
      popLayer(layer);
    });
  });

  // A new filter or search can take a square off the map while its machine is still in the
  // fleet: every claim on it goes, so the tooltip never points at nothing, and a claim on
  // the squares still shown survives. A user effect runs after the render effects that
  // re-drew the list, so the removed square is already detached here, in the same update.
  createEffect(() => {
    props.paged;
    untrack(prune);
  });

  return (
    <div class="machine-map">
      <MachineGroups
        paged={props.paged}
        allBoxes={props.allBoxes}
        token={props.token}
        notify={props.notify}
        onWritten={props.onWritten}
        canWrite={props.canWrite}
        body={(ids, label) => (
          <ul class="machine-map__tiles" aria-label={label()}>
            <For each={ids()}>
              {(id) => (
                <Show when={props.byId.get(id)}>
                  {(box) => (
                    <MachineMapSquare
                      box={box()}
                      mode={props.mode}
                      large={large()}
                      describedBy={tip()?.id === id ? tipId : undefined}
                      onHold={(anchor, h) =>
                        h === 'hover' ? hover({ id, anchor }, true) : focus({ id, anchor }, true)
                      }
                      onRelease={(anchor, h) =>
                        h === 'hover' ? hover({ id, anchor }, false) : focus({ id, anchor }, false)
                      }
                      onSelect={select}
                    />
                  )}
                </Show>
              )}
            </For>
          </ul>
        )}
      />
      <Show when={tip()}>
        {(open) => (
          <Show when={props.byId.get(open().id)}>
            {(box) => (
              <MachineMapTip
                id={tipId}
                box={box()}
                anchor={open().anchor}
                onPointerEnter={() => hoverTip(true)}
                onPointerLeave={() => hoverTip(false)}
              />
            )}
          </Show>
        )}
      </Show>
      <MachineMapLegend
        entries={mapLegend(props.boxes, props.mode, t)}
        hint={t('fleet.machines.map_hint')}
      />
    </div>
  );
}
